import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_TREASURY,
  ROUTE_MAKE_PAYMENT,
  ROUTE_ORDERS,
  ROUTE_PAYABLE,
  ROUTE_TRANSFER_FUNDS
} from '@klear/constants'
import type { Payable } from '@klear/payable/payable'
import {
  PAYABLE_ROW_ACTIONS,
  payableIsOutbound,
  payableTypeLabel,
  SCHEDULED_PAYABLE_ACTIONS,
  type PayableLifecycleAction,
  type PayableListBucket
} from '@klear/payable/list-display'
import { KlearDate } from '@klear/date'
import toNumber from 'lodash/toNumber'
import { ButtonLink } from '@klear/ui/components/button-link'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { Building2, CircleCheck, CircleDollarSign, Clock, FileText, MoveHorizontal, Send } from 'lucide-react'
import { SegmentedControl } from '@klear/ui/components/segmented-control'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { DataTable } from '@klear/ui/components/data-table'
import type { ColumnDef } from '@tanstack/react-table'
import { trpc } from '@klear/ui/lib/trpc'
import { DataTableColumnHeader } from '@klear/ui/components/data-table-column-header'
import { Badge } from '@klear/ui/components/badge'
import { PaymentStatusBadge } from '@klear/ui/components/payment-status-badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { PayableActions } from '@/app/payables/payable-actions'
import { PayableLinkedEvents } from '@/app/payables/payable-linked-events'
import { PayableLinkedTrades } from '@/app/payables/payable-linked-trades'
import { PayablesInsights } from '@/app/payables/payables-insights'
import { usePayables } from '@klear/ui/hooks/use-payables'
import { paymentIsFailed } from '@klear/payment/status'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

function payableAmountMinor(payable: Payable): number {
  return toNumber(payable.amount.amount.toString())
}

function payableDisplayStatus(payable: Payable): string {
  return payable.paymentStatus ?? payable.status
}

function payablePostedDate(payable: Payable): KlearDate | null {
  return payable.postedAt?.toKlearDate() ?? null
}

export default function PayablesPage() {
  useDocumentTitle('Payables')
  const { user } = useAuth()
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const [activeTab, setActiveTab] = useState<PayableListBucket>('scheduled')
  const utils = trpc.useUtils()
  const { payables } = usePayables(parseInt(traderId || '0'))
  const { data: invoiceList } = trpc.invoice.list.useQuery({})
  const invoices = invoiceList?.invoices ?? []
  const canCreatePayable = user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_ADD)
  const canDoTreasury = user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)

  const handlePaymentSuccess = () => {
    void utils.payable.list.invalidate()
  }

  const tabPayables = useMemo(() => {
    const buckets: Record<PayableListBucket, Payable[]> = {
      scheduled: [],
      draft: [],
      sent: []
    }
    for (const payable of payables) {
      buckets[payable.listBucket].push(payable)
    }
    return buckets
  }, [payables])

  return (
    <section className="flex flex-col gap-8 py-8">
      {(canCreatePayable || canDoTreasury) && (
        <div className="flex">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-4">
              {canCreatePayable && (
                <ButtonLink Link={Link} variant="outline" to={`${urlPrefixApp}/${ROUTE_PAYABLE}`}>
                  <CircleDollarSign className="mr-2 h-4 w-4" />
                  Create Payable
                </ButtonLink>
              )}
              {canDoTreasury && (
                <>
                  <ButtonLink Link={Link} variant="outline" to={`${urlPrefixApp}/${ROUTE_MAKE_PAYMENT}`}>
                    <Send className="mr-2 h-4 w-4" />
                    Make a Payment
                  </ButtonLink>
                  <ButtonLink Link={Link} variant="outline" to={`${urlPrefixApp}/${ROUTE_TRANSFER_FUNDS}`}>
                    <MoveHorizontal className="mr-2 h-4 w-4" />
                    Transfer Funds
                  </ButtonLink>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <PayablesInsights payables={payables} invoices={invoices} />
      <SegmentedControl
        className="w-full max-w-xl"
        options={[
          {
            value: 'draft',
            label: `Draft (${tabPayables.draft.length})`,
            icon: <CircleCheck className="h-4 w-4" />
          },
          {
            value: 'scheduled',
            label: `Scheduled (${tabPayables.scheduled.length})`,
            icon: <Clock className="h-4 w-4" />
          },
          { value: 'sent', label: `Sent (${tabPayables.sent.length})`, icon: <CircleCheck className="h-4 w-4" /> }
        ]}
        value={activeTab}
        onChange={setActiveTab}
      />

      <PayableTable
        key={activeTab}
        activeTab={activeTab}
        payables={tabPayables[activeTab]}
        onPaymentActionSuccess={handlePaymentSuccess}
      />
    </section>
  )
}

interface PayableTableProps {
  payables: Payable[]
  activeTab: PayableListBucket
  onPaymentActionSuccess: () => void
}

const ROW_ACTIONS_BY_TAB: Record<PayableListBucket, readonly PayableLifecycleAction[]> = {
  draft: PAYABLE_ROW_ACTIONS,
  scheduled: SCHEDULED_PAYABLE_ACTIONS,
  sent: []
}

function PayableTable({ payables, activeTab, onPaymentActionSuccess }: PayableTableProps) {
  const { traderId } = useParams()
  const { urlPrefixApp, trader } = useTrader(traderId)
  const navigate = useNavigate()
  const traderOrganizationName = trader?.name ?? ''

  const getMethodBadge = (payable: Payable) => (
    <div className="w-full">
      <Badge
        variant="outline"
        className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900 justify-center flex flex-col items-center w-full text-center gap-1"
      >
        {payable.paymentNetwork}
        {payable.last4 ? <span className="text-xs text-muted-foreground">...{payable.last4}</span> : null}
      </Badge>
    </div>
  )

  const getStatusBadge = (payable: Payable) => {
    const status = payableDisplayStatus(payable)
    if (activeTab === 'draft') {
      if (paymentIsFailed(status)) {
        return (
          <Badge
            variant="secondary"
            className="text-xs bg-red-50 text-red-800 border-red-500 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
          >
            Failed
          </Badge>
        )
      }
      return (
        <Badge
          variant="secondary"
          className="text-xs bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-900"
        >
          Draft
        </Badge>
      )
    }
    if (payable.isSending) {
      return <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">Sending</Badge>
    }
    if (paymentIsFailed(status) && payable.stripePaymentErrorMessage) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={0}>
                <PaymentStatusBadge status={status} className="text-xs" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{payable.stripePaymentErrorMessage}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    return <PaymentStatusBadge status={status} className="text-xs" />
  }

  const getDaysEarly = (payable: Payable) => {
    const postedDate = payablePostedDate(payable)
    if (postedDate == null) return null
    return payable.effectiveDate.diff(postedDate, 'day')
  }

  const getActions = (payable: Payable) => {
    if (!traderId) return null
    return (
      <PayableActions
        payable={payable}
        traderId={parseInt(traderId, 10)}
        onActionSuccess={onPaymentActionSuccess}
        allowedActions={ROW_ACTIONS_BY_TAB[activeTab]}
      />
    )
  }

  const columns: ColumnDef<Payable>[] = [
    {
      id: 'idDisplay',
      accessorFn: (row) => row.idDisplay,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Payment ID" />,
      cell: ({ row }) => <div className="text-sm text-foreground">{row.original.idDisplay}</div>,
      enableSorting: true
    },
    {
      id: 'createdAt',
      accessorFn: (row) => row.createdAt?.toString('MMM DD') ?? '',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created Date" />,
      cell: ({ row }) => (
        <div className="text-sm text-foreground">{row.original.createdAt?.toString('MMM DD') ?? '-'}</div>
      ),
      enableSorting: true
    },
    ...(activeTab === 'sent'
      ? ([
          {
            id: 'postedDate',
            accessorFn: (row) => payablePostedDate(row)?.toString('MMM DD') ?? '',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Posted Date" />,
            cell: ({ row }) => (
              <div className="text-sm text-foreground">
                {payablePostedDate(row.original)?.toString('MMM DD') ?? '-'}
              </div>
            ),
            enableSorting: true
          },
          {
            id: 'daysEarly',
            accessorFn: (row) => getDaysEarly(row) ?? '',
            header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Days Early" />,
            cell: ({ row }) => {
              const days = getDaysEarly(row.original)
              return <div className="text-right text-sm text-foreground">{days == null ? '-' : days}</div>
            },
            enableSorting: true,
            sortingFn: 'alphanumeric'
          }
        ] satisfies ColumnDef<Payable>[])
      : ([
          {
            id: 'scheduledDate',
            accessorFn: (row) => row.effectiveDate.toString('MMM DD'),
            header: ({ column }) => <DataTableColumnHeader column={column} title="Scheduled Date" />,
            cell: ({ row }) => (
              <div className="text-sm text-foreground">{row.original.effectiveDate.toString('MMM DD')}</div>
            ),
            enableSorting: true
          }
        ] satisfies ColumnDef<Payable>[])),
    {
      id: 'recipient',
      accessorFn: (row) =>
        payableIsOutbound(row) ? row.destinationAccountOrganizationName || 'Unknown' : traderOrganizationName,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Recipient" />,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2 font-semibold">
          <Building2 className="h-4 w-4 mr-1" />
          {payableIsOutbound(row.original)
            ? row.original.destinationAccountOrganizationName || 'Unknown'
            : traderOrganizationName}
        </div>
      ),
      enableSorting: true
    },
    {
      id: 'type',
      accessorFn: (row) => payableTypeLabel(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => <PayableLinkedTrades payable={row.original} urlPrefixApp={urlPrefixApp} />,
      enableSorting: false
    },
    {
      id: 'method',
      accessorFn: (row) => `${row.paymentNetwork}${row.last4 ? ` ...${row.last4}` : ''}`,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
      cell: ({ row }) => getMethodBadge(row.original),
      enableSorting: false
    },
    {
      id: 'amount',
      accessorFn: (row) => payableAmountMinor(row),
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Amount" />,
      cell: ({ row }) => <PayableLinkedEvents payable={row.original} urlPrefixApp={urlPrefixApp} />,
      enableSorting: true,
      sortingFn: 'alphanumeric'
    },
    {
      id: 'requestedBy',
      accessorFn: (row) =>
        [row.createdBy?.firstName, row.createdBy?.lastName?.slice(0, 1)?.toUpperCase()].filter(Boolean).join(' '),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Requested by" />,
      cell: ({ row }) => {
        const createdBy = row.original.createdBy
        if (!createdBy?.firstName) return <div className="text-center text-muted-foreground">-</div>
        const lastInitial = createdBy.lastName?.slice(0, 1)?.toUpperCase() ?? ''
        return (
          <div className="text-center">
            {createdBy.firstName} {lastInitial ? `${lastInitial}.` : ''}
          </div>
        )
      },
      enableSorting: false
    },
    {
      id: 'status',
      accessorFn: (row) => payableDisplayStatus(row),
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Status" />,
      cell: ({ row }) => <div className="flex justify-end">{getStatusBadge(row.original)}</div>,
      enableSorting: true
    },
    ...(activeTab === 'sent'
      ? ([
          {
            id: 'receipt',
            header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Receipt" />,
            cell: ({ row }) =>
              row.original.receiptUrl ? (
                <div className="flex justify-end">
                  <a
                    href={row.original.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-colors"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </a>
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="w-4 h-4" />
                </div>
              ),
            enableSorting: false
          }
        ] satisfies ColumnDef<Payable>[])
      : []),
    {
      id: 'actions',
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="" />,
      cell: ({ row }) => (
        <div
          className="flex justify-end"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {getActions(row.original)}
        </div>
      ),
      enableSorting: false
    }
  ]

  return (
    <DataTable
      data={payables}
      columns={columns}
      filters={[]}
      onRowClick={(payable) => {
        if (payable.orderId != null) {
          navigate(`${urlPrefixApp}/${ROUTE_ORDERS}/${payable.orderId}?orderEventId=${payable.operationalId}`)
        } else if (payable.id != null) {
          navigate(`${urlPrefixApp}/${ROUTE_PAYABLE}/${payable.id}`)
        }
      }}
    />
  )
}
